import React from "react";
import {
  Alert,
  Box,
  Typography,
  Paper,
  Fade,
  Grow,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { TradeloftGlyph } from "../../trades/TradeloftMark";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useForm from "../../forms/hooks/useForm";
import UserForm from "../components/UserForm";
import { initialSignupForm } from "../helpers/initialForms/initialSignupForm";
import useHandleUsers from "../hooks/useHandleUsers";
import signupSchema from "../models/Joi/signupSchema";
import { useUser } from "../providers/UserProvider";
import ROUTES from "../../router/routesModel";
import { intendedDestination } from "../../router/intendedDestination";
import { isSupabaseConfigured } from "../../lib/supabaseClient";
import { useKeyboardInset } from "../../native/keyboardInset";
import { dvh } from "../../styles/viewportHeight";

/** The page's resting vertical padding, in pixels — MUI's `py: 4`. */
const PAGE_PAD_Y_PX = 32;

const SignUpPage = () => {
  const { user } = useUser();
  const { handleSignup, handleGoogleLogin, value: handleValue } = useHandleUsers();
  // #197. `error` is the server's own sentence, already stored by
  // handleSignup's catch (`requestStatus(false, msg, null)`,
  // users/hooks/useHandleUsers.js:258) and already returned in `value`
  // (:320) — this page just never read it. So the entire report of a rejected
  // registration was a snackbar that auto-hides after 3s
  // (providers/SnackBarProvider.jsx:57), on the one form every new user has to
  // get through. Look away for three seconds and a refused signup is
  // indistinguishable from having done nothing.
  //
  // The global axios interceptor cannot stand in for it either: it raises a
  // refusal such as this form's commonest failure, a duplicate email (a 409,
  // server/users/usersRestController.js), only when no screen has shown it
  // (B-31), and handleSignup's own snackbar has, so it adds nothing lasting.
  //
  // Nothing else on this page writes it: handleGoogleLogin only snacks and
  // never touches requestStatus, so the OAuth path is unaffected. It clears on
  // the next attempt, when handleSignup's own requestStatus call overwrites it.
  const { isLoading: submitting, error: signupError } = handleValue;
  const navigate = useNavigate();
  // Where sign-in was taking the reader (RequireAuth's `from`), handed on by
  // LoginPage, so a brand-new account still lands there.
  const location = useLocation();
  const backToLogin = () => {
    if (location.state?.from) navigate(ROUTES.LOGIN, { state: { from: location.state.from } });
    else navigate(ROUTES.LOGIN);
  };
  // Android 15+ (API 36) draws edge-to-edge and no longer resizes the WebView
  // when the IME opens: this Box stays a full 100dvh tall, so the lower half of
  // the sign-up form — including Submit — ends up behind the keyboard with no
  // document left to scroll. See src/native/keyboardInset.js.
  const keyboardInset = useKeyboardInset();
  const { value, ...rest } = useForm(
    initialSignupForm,
    signupSchema,
    handleSignup
  );
  const { handleInputChange, handleReset, onSubmit, setData, validateForm } =
    rest;
  
  if (user) return <Navigate replace to={intendedDestination(location, ROUTES.ROOT)} />;
  
  return (
    <Box
      sx={{
        minHeight: dvh(100),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        pt: `${PAGE_PAD_Y_PX}px`,
        // Grows by the reported keyboard height so the form always has that
        // much room to scroll into. `overflow: hidden` only clips the ::before
        // glow — the Box itself is min-height, so it grows and the document
        // scrolls. 0 on web, where nothing ever sets the inset.
        pb: `${PAGE_PAD_Y_PX + keyboardInset}px`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 30% 50%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 70% 80%, rgba(138, 43, 226, 0.3), transparent 50%)",
          animation: "pulse 8s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%, 100%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.8,
          },
        },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 700, px: 2, position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Fade in timeout={600}>
          <Button
            startIcon={<ArrowBack />}
            onClick={backToLogin}
            sx={{
              mb: 2,
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Back to Login
          </Button>
        </Fade>

        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                py: 4,
                px: 4,
                textAlign: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  right: 0,
                  height: 2,
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                },
              }}
            >
              <Grow in timeout={1000}>
                {/* Grow needs a child that can hold a ref; the glyph is a
                    plain function component, so it sits in a Box. */}
                <Box sx={{ display: "inline-flex", mb: 1 }}>
                  <TradeloftGlyph size={52} title="Tradeloft" />
                </Box>
              </Grow>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  mb: 0.5,
                }}
              >
                Create Your Account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontWeight: 300,
                }}
              >
                Join the Tradeloft community today
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ p: 4 }}>
              {/* #197. Above the fields, so the reason the account was not
                  created is read before the form is re-read. MUI's Alert
                  carries role="alert", so it is announced as well as shown.
                  The string is the server's, unmodified. */}
              {signupError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {signupError}
                </Alert>
              )}
              <UserForm
                title=""
                data={value.data}
                errors={value.errors}
                hasFormErrors={validateForm}
                onInputChange={handleInputChange}
                onReset={handleReset}
                onSubmit={onSubmit}
                setData={setData}
                submitting={submitting}
              />

              {isSupabaseConfigured() && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", my: 2.5 }}>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.12)" }} />
                    <Typography sx={{ px: 2, color: "#64748b", fontSize: "0.82rem", fontWeight: 600 }}>or</Typography>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.12)" }} />
                  </Box>
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={submitting}
                    style={{
                      width: "100%",
                      background: "#111827",
                      color: "#e2e8f0",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 14,
                      padding: "13px",
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: submitting ? "default" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.01 24.01 0 000 21.56l7.98-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    Sign up with Google
                  </button>
                </>
              )}
            </Box>

            {/* Footer Wave */}
            <Box
              sx={{
                height: 8,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            />
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

export default SignUpPage;
